"use client";

import { CtaArrow } from "@/components/editorial/cta-arrow";

import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  DATA_LABEL_CLS,
  EDITORIAL_DARK_TOKENS,
  EDITORIAL_LIGHT_TOKENS,
  GROUND,
  INK,
  SURFACE_DARK,
} from "@/components/editorial";
import { usePicks } from "./picks";

/**
 * One reviewable marketing component on the /concept board.
 *
 * IDs are stable and page-prefixed (`HRO-001`, `TRU-004` …) so a component can
 * be named precisely in feedback. Never renumber an existing id; when one is
 * retired leave the gap rather than reusing it.
 */
export type ConceptSpec = {
  id: string;
  title: string;
  /** One line shown in the tooltip: what it is and where the idiom comes from. */
  note: string;
  /** The ground the component paints for itself. Drives the frame's chrome
   *  colours so the id chip stays legible on either surface. */
  surface?: "ground" | "dark";
  /** `full` = the component owns the full column width (sections, bands).
   *  `contained` = rendered inside the editorial container rail (cards,
   *  tables, specimens). `narrow` = a single-column element (dialogs,
   *  buttons, labels) centred at max 36rem. */
  width?: "full" | "contained" | "narrow";
  /** Review-only metadata shown in the board chrome, never inside the
   * specimen itself. `prototype` means the local state can be exercised;
   * `static` identifies a visual study with deliberately inert controls. */
  interaction?: "static" | "prototype";
  /** Stable id of the source study when this entry is a visual variant. */
  variantOf?: string;
  /** Concise state or tone name shown alongside the reference id. */
  state?: string;
  /** Route where this spec can be viewed as its own full page (page-scale
   * assemblies only). Rendered as a link in the board's caption rail. */
  href?: string;
  render: () => ReactNode;
};

export type ConceptGroup = {
  id: string;
  prefix: string;
  title: string;
  blurb: string;
  specs: ConceptSpec[];
};

/**
 * Editorial rule/label tokens the components rely on (DATA_LABEL_CLS and the
 * dotted hairlines read `--rule`, `--type-label-small` and `--type-data`).
 * Light variant for cream grounds; dark variant for the deep surface.
 */
export const LIGHT_TOKENS = EDITORIAL_LIGHT_TOKENS;
export const DARK_TOKENS = EDITORIAL_DARK_TOKENS;

/**
 * The frame around each component: an always-visible reference chip in the
 * top-left corner (drawing-sheet style), a tooltip on hover/focus with the
 * title and note, click-to-copy on the id, and a caption rail underneath so
 * the board scans as a numbered catalogue.
 */
export function SpecFrame({ spec }: { spec: ConceptSpec }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dark = spec.surface === "dark";
  const width = spec.width ?? "full";
  const picksApi = usePicks();
  const picked = picksApi?.isPicked(spec.id) ?? false;
  const reviewLabels = [
    spec.interaction === "static"
      ? "Static study"
      : spec.interaction === "prototype"
        ? "Prototype"
        : null,
    spec.variantOf ? `Variant of ${spec.variantOf}` : null,
    spec.state ?? null,
  ].filter((label): label is string => Boolean(label));
  const reviewSummary = reviewLabels.join(" · ");

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(spec.id);
    } catch {
      // Clipboard may be unavailable (permissions / insecure context).
    }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1100);
  }, [spec.id]);

  const chipBg = dark ? GROUND : SURFACE_DARK;
  const chipFg = dark ? INK : GROUND;

  return (
    <section
      id={spec.id}
      data-ref={spec.id}
      aria-labelledby={`${spec.id}-caption`}
      className="concept-spec relative scroll-mt-6"
      style={dark ? DARK_TOKENS : LIGHT_TOKENS}
    >
      <div
        className="concept-spec-stage relative"
        style={{
          backgroundColor: dark ? SURFACE_DARK : GROUND,
          color: dark ? GROUND : INK,
        }}
      >
        {/* Reference chip + tooltip. Sits above the component so the id is
            always readable; the tooltip carries the title and note. */}
        <div className="concept-chip-anchor absolute top-3 left-3 z-[30] flex items-start gap-1.5">
          <button
            type="button"
            className="concept-chip"
            aria-label={`${spec.id} · ${spec.title}.${reviewSummary ? ` ${reviewSummary}.` : ""} Click to copy the id.`}
            aria-describedby={`${spec.id}-tip`}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onClick={copy}
            style={{ backgroundColor: chipBg, color: chipFg }}
          >
            <span aria-hidden="true" className="concept-chip-dot" style={{ backgroundColor: "var(--theme-accent)" }} />
            {copied ? "Copied" : spec.id}
          </button>
          {picksApi ? (
            <button
              type="button"
              className="concept-pick"
              aria-pressed={picked}
              aria-label={picked ? `Remove ${spec.id} from picks` : `Add ${spec.id} to picks`}
              onClick={() => picksApi.toggle(spec.id)}
              style={{ backgroundColor: chipBg, color: picked ? "var(--theme-accent)" : chipFg }}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true" width="10" height="10">
                <path
                  d="M8 1.8 L9.9 5.7 L14.2 6.3 L11.1 9.3 L11.8 13.6 L8 11.6 L4.2 13.6 L4.9 9.3 L1.8 6.3 L6.1 5.7 Z"
                  fill={picked ? "currentColor" : "none"}
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : null}
          <span
            id={`${spec.id}-tip`}
            role="tooltip"
            className="concept-tip"
            data-open={open ? "true" : "false"}
          >
            <strong className="concept-tip-title">{spec.title}</strong>
            <span className="concept-tip-note">{spec.note}</span>
            <span className="concept-tip-meta">
              {spec.id} · {width} · {dark ? "dark surface" : "cream ground"}
              {reviewSummary ? ` · ${reviewSummary}` : ""} · click to copy
            </span>
          </span>
        </div>

        <div
          className={
            width === "full"
              ? "concept-stage-full"
              : width === "contained"
                ? "mx-auto w-full max-w-screen-xl px-5 py-12 sm:px-6 md:px-14 md:py-16"
                : "mx-auto w-full max-w-[36rem] px-5 py-12 sm:px-6 md:py-16"
          }
        >
          {spec.render()}
        </div>
      </div>

      {/* Caption rail — drawing-sheet style numbered catalogue row. */}
      <div
        className="flex min-h-[2.4rem] flex-wrap items-center gap-x-4 gap-y-1 border-y border-dotted border-[var(--rule)] px-3 py-2"
        style={{ ...LIGHT_TOKENS, backgroundColor: GROUND, color: INK }}
      >
        <span className={`[font-variant-numeric:tabular-nums] text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>{spec.id}</span>
        <span id={`${spec.id}-caption`} className={`text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)] ${DATA_LABEL_CLS}`}>
          {spec.title}
        </span>
        {reviewLabels.length > 0 ? (
          <span className="flex flex-wrap items-center gap-1.5">
            {reviewLabels.map((label) => (
              <span
                key={label}
                className="whitespace-nowrap border border-dotted border-[var(--rule)] px-1.5 py-0.5 text-[0.65rem] leading-[1.2] font-medium tracking-[0.08em] text-[color-mix(in_srgb,var(--theme-ink)_72%,transparent)] uppercase"
              >
                {label}
              </span>
            ))}
          </span>
        ) : null}
        {spec.href ? (
          <a
            href={spec.href}
            className={`whitespace-nowrap text-[var(--theme-accent-text)] underline-offset-4 hover:underline ${DATA_LABEL_CLS}`}
          >
            Full page <CtaArrow />
          </a>
        ) : null}
        <span className="ml-auto text-[0.78rem] leading-[1.4] text-[color-mix(in_srgb,var(--theme-ink)_78%,transparent)]">
          {spec.note}
        </span>
      </div>
    </section>
  );
}
